import React, { useEffect, useState, useCallback } from "react";
import { API_URLS } from "../../../../apiUrl";
import axiosInstance from "../../../../components/axiosInstance";
import Pagination from "@mui/material/Pagination";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

interface CsvErrorRecord {
  street_id: string | null;
  ferule_size: string | null;
  consumer_name: string | null;
  holding_number: string | null;
  connection_number: string | null;
  opening_due_amount: number | string | null;
  connection_start_date: string | null;
  consumer_category_name: string | null;
}

interface CsvErrorRow {
  id: number;
  record: CsvErrorRecord;
  error_reason: string;
  error_number: string;
  created_at: string;
  csv_id: number;
  file_name: string | null;
}

interface ApiResponse {
  error: boolean;
  message?: string;
  data?: {
    rows: CsvErrorRow[];
    pagination: {
      page: number;
      limit: number;
      lastPage: number;
      totalValues: number;
    };
  };
}

const ErrorReasonWithPopup = ({ reason }: { reason: string }) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
   <div className="flex items-center gap-1.5 w-full">
  
  <span className="flex-1 text-[12px] text-gray-600 truncate leading-tight">
    {reason?.length > 10 ? `${reason.substring(0, 10)}...` : reason}
  </span>

  <button
    onClick={(e) => {
      e.stopPropagation();
      setShowModal(true);
    }}
    title="View full error details"
    aria-label="Show error details"
    className="group flex-shrink-0 flex items-center justify-center w-[18px] h-[18px] rounded-full border border-blue-300 bg-blue-50 hover:bg-blue-600 hover:border-blue-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1"
  >
    <svg
      className="w-[9px] h-[9px] text-blue-600 group-hover:text-white transition-colors"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
        clipRule="evenodd"
      />
    </svg>
  </button>

</div>

     {showModal && (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[1px] p-4"
    onClick={() => setShowModal(false)}
  >
    <div
      className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-[fadeIn_.2s_ease]"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-red-50 to-white">
        
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-red-100">
            <svg
              className="w-4 h-4 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
          </div>

          <div>
            <h3 className="text-sm font-bold text-gray-800">
              Error Details
            </h3>
            <p className="text-[10px] text-gray-400">
              Full error reason
            </p>
          </div>
        </div>

      </div>

      {/* Body */}
      <div className="px-5 py-5 max-h-[320px] overflow-y-auto">
        <div className="bg-red-50 border border-red-100 rounded-xl p-4">
          <p className="text-[13px] text-red-800 leading-relaxed font-medium break-words whitespace-pre-wrap">
            {reason}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 pb-5">
        <button
          onClick={() => setShowModal(false)}
          className="w-full py-2.5 rounded-xl bg-gray-800 hover:bg-gray-900 text-white text-[12px] font-semibold transition focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}
    </>
  );
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    return date
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true, // set false if you want 24-hour format
      })
      .replace(/\//g, "-");
  } catch {
    return "Invalid Date";
  }
};

const Csvfaildstatus = () => {
  const [rows, setRows] = useState<CsvErrorRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    lastPage: 1,
    totalValues: 0,
  });

  const fetchErrorLog = useCallback(async (page: number) => {
    setLoading(true);
    try {
      const response = await axiosInstance.post<ApiResponse>(
        API_URLS.uploadcsvFailed,
        { page, limit: 50 }
      );

      if (response.data.error) {
        toast.error(response.data.message || "Failed to load error logs");
        return;
      }

      if (response.data.data && Array.isArray(response.data.data.rows)) {
        setRows(response.data.data.rows);
        setPagination(
          response.data.data.pagination || {
            page: 1,
            limit: 50,
            lastPage: 1,
            totalValues: response.data.data.rows.length,
          }
        );
      } else {
        toast.error("Invalid response format from server");
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || "Something went wrong";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchErrorLog(1);
  }, [fetchErrorLog]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    fetchErrorLog(page);
  };

  return (
    <div className="w-full px-4 py-6 sm:px-6">
      <ToastContainer position="top-right" autoClose={4000} />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-red-100">
            <svg
              className="w-4 h-4 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-800">CSV Failed Records</h1>
            <p className="text-[10px] text-gray-400 mt-0.5">
              {pagination.totalValues} total failed entries found
            </p>
          </div>
        </div>

        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-semibold bg-red-50 text-red-600 border border-red-200 w-fit">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
          Failed Upload Log
        </span>
      </div>

      <div className="w-full rounded-xl border border-gray-200 shadow-sm bg-white overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="py-2 px-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Sl. No.
              </th>
              <th className="py-2 px-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Uploaded File Name
              </th>
              <th className="py-2 px-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Connection ID
              </th>
              <th className="py-2 px-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Consumer Name
              </th>
              <th className="py-2 px-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Uploaded Date
              </th>
              <th className="py-2 px-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Error Code
              </th>
              <th className="py-2 px-3 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Error Reason
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  {Array.from({ length: 7 }).map((__, j) => (
                    <td key={j} className="py-2 px-3">
                      <div className="h-2.5 bg-gray-100 rounded w-full" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-12 text-center">
                  <div className="flex flex-col items-center gap-2 text-gray-400">
                    <svg
                      className="w-8 h-8 text-gray-200"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <p className="text-xs font-medium">No failed records found</p>
                    <p className="text-[10px]">All CSV rows processed successfully</p>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr
                  key={row.id}
                  className={`hover:bg-red-50/40 transition-colors duration-150 ${
                    idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                  }`}
                >
                  <td className="py-2 px-3 whitespace-nowrap">
                    <span className="text-[12px] font-mono text-gray-600">
                      {(pagination.page - 1) * pagination.limit + idx + 1}
                    </span>
                  </td>

                  <td className="py-2 px-3 whitespace-nowrap">
                    <span className="text-[11px] font-medium text-gray-800">
                      {row.file_name ?? <span className="text-gray-300">—</span>}
                    </span>
                  </td>

                  <td className="py-2 px-3 whitespace-nowrap">
                    <span className="text-[11px] text-gray-600">
                      {row.record?.connection_number ?? <span className="text-gray-300">—</span>}
                    </span>
                  </td>

                  <td className="py-2 px-3 whitespace-nowrap">
                    <span className="text-[11px] text-gray-800">
                      {row.record?.consumer_name ?? <span className="text-gray-300">—</span>}
                    </span>
                  </td>

                  <td className="py-2 px-3 whitespace-nowrap">
                    <span className="text-[11px] text-gray-600">
                      {formatDate(row.created_at)}
                    </span>
                  </td>

                  <td className="py-2 px-2 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-red-100 text-red-700 border border-red-200">
                      <span className="w-1 h-1 rounded-full bg-red-500" />
                      {row.error_number}
                    </span>
                  </td>

                  <td className="py-2 px-3 whitespace-nowrap">
                    <ErrorReasonWithPopup reason={row.error_reason} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

        <div className="mt-4 flex justify-center">
          <Pagination
            count={pagination.lastPage}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
            size="small"
            shape="rounded"
            siblingCount={1}
            boundaryCount={1}
          />
        </div>
    </div>
  );
};

export default Csvfaildstatus;